import React, { useState, useEffect, useCallback } from "react";
import DealershipDetails from "./dealershipdetails/DealershipDetails";
import MerchantId from "./merchantId/MerchantId";
import { withTranslation } from "react-i18next";
import AcceptTermsAndConditions from "./termsAndConditions/AcceptTermsAndConditions";
import { InfoIconWithMessage } from "../../shared/infoIcon/InfoIcon";
import LoadTermsAndConditions from "./termsAndConditions/LoadTermsAndConditions";
import EmpMembership from "./EmpMembership";
import DealerDeliveryRangeAndPrice from "./deliveryRanges/dealer/DealerDeliveryRanges";
import SubNav from "../../shared/subnav/SubNav";
import HasPermissionTo from "../../common/HasPermissionTo";
import CustomerContract from "./customerContract/CustomerContract";
import OptIn from "./optIn/OptIn";
import PaymentThreshold from "./paymentThreshold/PaymentThreshold";
import LeadTimes from "./leadTimes/LeadTimes";
import UpperCaseText from "../../utils/UpperCaseText";
import { IS_VNM_MARKET, IS_ZAF_MARKET } from "../../utils/EmpUtil";

function DealerBusinessSettings(props) {
  const [selectedTab, setSelectedTab] = useState(props.startingTab);
  const [renderPage, setRenderPage] = useState(false);

  const getTabs = useCallback(() => {
    return [
      {
        menuName: props.t("ProgressBar.empMembership"),
        enabled: true,
        id: "empMembership",
      },
      {
        menuName: props.t("ProgressBar.dealershipDetails"),
        enabled: true,
        id: "dealership-details",
      },
      {
        menuName: props.t("ProgressBar.deliveryRanges"),
        enabled: true,
        id: "deliveryRanges",
        hide:
          !props.user.permissions.rolePermissions.hasOwnProperty(
            "dealerDeliveryRangesTab"
          ) || !props.user.hasNscCompletedBasicPAs,
      },
      {
        menuName:
          IS_ZAF_MARKET(props.user.market) ||
          IS_VNM_MARKET(props.user.market)
            ? props.t("StoreId.header")
            : props.t("ProgressBar.storeId"),
        enabled: true,
        id: "merchantId",
        hide: !props.user.permissions.rolePermissions.hasOwnProperty(
          "merchantIdTab"
        ),
      },
      {
        menuName: props.t("ProgressBar.customerContract"),
        enabled: true,
        id: "customerContract",
        hide:
          !props.user.permissions.rolePermissions.hasOwnProperty(
            "customerContractTab"
          ) || !props.user.hasNscCompletedBasicPAs,
      },
      {
        menuName: props.t("ProgressBar.optIn"),
        enabled: true,
        id: "optIn",
        hide:
          !props.user.permissions.rolePermissions.hasOwnProperty(
            "optInTab"
          ) || !props.user.hasNscCompletedBasicPAs,
      },
      {
        menuName: props.t("ProgressBar.leadTimes"),
        enabled: true,
        id: "leadTimes",
        hide: !props.user.permissions.rolePermissions.hasOwnProperty(
          "leadTimesTab"
        ),
      },
      {
        menuName: UpperCaseText(props.t("PaymentThreshold.tabCaption")),
        enabled: true,
        id: "paymentThreshold",
        hide: !props.user.permissions.rolePermissions.hasOwnProperty(
          "paymentThresholdTab"
        ),
      },
    ];
  }, [props.t, props.user]);

  const getVisibleTabs = useCallback(() => {
    return getTabs().filter(tab => !tab.hide);
  }, [getTabs]);

  const handleTabChange = useCallback((event, newSelectedTab) => {
    setSelectedTab(newSelectedTab);
    props.user.updateBusinessSettingsTab(newSelectedTab);
    props.resetFromPending();
  }, [props.user, props.resetFromPending]);

  const renderTermsConditionsTab = useCallback(() => {
    return props.isDealerAcceptingTerms ? (
      <AcceptTermsAndConditions {...props} />
    ) : (
      <>
        <div className="pagePaddingLeft pagePaddingTop">
          <InfoIconWithMessage
            message={props.t("AcceptTermsConditions.subheader")}
          />
        </div>
        <LoadTermsAndConditions {...props} />
        <HasPermissionTo
          perform={["empMembership:view"]}
          permissions={props.user.permissions.rolePermissions}
          render={() => {
            return <EmpMembership {...props} />;
          }}
        />
      </>
    );
  }, [props]);

  const visibleTabs = getVisibleTabs();
  const currentTab = visibleTabs[selectedTab];

  useEffect(() => {
    const initialTab = props.user.businessSettingsPage !== undefined
      ? props.user.businessSettingsPage
      : selectedTab;

    setSelectedTab(initialTab);

    if (props.user.updateBusinessSettingsTab) {
      props.user.updateBusinessSettingsTab(initialTab);
    }

    setRenderPage(true);
  }, []);

  useEffect(() => {
    if (props.user.businessSettingsPage !== undefined) {
      setSelectedTab(props.user.businessSettingsPage);
    }
  }, [props.user.businessSettingsPage]);

  if (!renderPage) {
    return null;
  }

  return (
    <>
      <div style={{ position: 'relative', zIndex: 100 }}>
        <SubNav
          dashboard={false}
          selectedTab={selectedTab}
          title={props.t("GlobalNavBar.settings")}
          action={handleTabChange}
          tabs={visibleTabs}
          restrictDropdown={props.restrictDropdown}
          {...props}
        />
      </div>
      <div className="pageWrapper">
        {currentTab?.id === "empMembership" && renderTermsConditionsTab()}
        {currentTab?.id === "dealership-details" && (
          <DealershipDetails {...props} />
        )}
        <HasPermissionTo
          perform={["dealerDeliveryRangesTab"]}
          permissions={props.user.permissions.rolePermissions}
          condition={currentTab?.id === "deliveryRanges"}
          render={() => {
            return <DealerDeliveryRangeAndPrice {...props} />;
          }}
        />
        <HasPermissionTo
          perform={["merchantIdTab"]}
          permissions={props.user.permissions.rolePermissions}
          condition={currentTab?.id === "merchantId"}
          render={() => {
            return <MerchantId {...props} fromQuickstart={false} />;
          }}
        />
        <HasPermissionTo
          perform={["customerContractTab"]}
          permissions={props.user.permissions.rolePermissions}
          condition={currentTab?.id === "customerContract"}
          render={() => {
            return <CustomerContract {...props} />;
          }}
        />
        <HasPermissionTo
          perform={["optInTab"]}
          permissions={props.user.permissions.rolePermissions}
          condition={currentTab?.id === "optIn"}
          render={() => {
            return <OptIn {...props} />;
          }}
        />
        <HasPermissionTo
          perform={["leadTimesTab"]}
          permissions={props.user.permissions.rolePermissions}
          condition={currentTab?.id === "leadTimes"}
          render={() => <LeadTimes {...props} />}
        />
        <HasPermissionTo
          perform={["paymentThresholdTab"]}
          permissions={props.user.permissions.rolePermissions}
          condition={currentTab?.id === "paymentThreshold"}
          render={() => <PaymentThreshold {...props} />}
        />
      </div>
    </>
  );
}

export default withTranslation("emp")(DealerBusinessSettings);
